import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { DsImprintModule, DsToastModule } from '@bmw-ds/components';
import { TranslateModule } from '@ngx-translate/core';
import { ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { dsAgGridTheme } from '@bmw-ds/components/ds-ag-grid-theme/ag-grid-theme';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { provideOAuthClient } from 'angular-oauth2-oidc';

import { routes } from './app.routes';
import { httpAuthenticationInterceptor } from './authentication.interceptor';
import { DowntimeBannerService } from './core/services/downtime-banner.service';
import { BackofficeService } from './core/services/backoffice.service';
import { CaseService } from './core/services/case.service';
import { DealerService } from './core/services/dealer.service';
import { LocaleService, registerSupportedLocales } from './core/services/locale.service';
import { ModalService } from './core/services/modal.service';
import { PreConditionService } from './core/services/pre-condition.service';
import { UserService } from './core/services/user.service';
import { DealerQueueService } from './core/services/v2/dealer-queue.service';
import { FleetService } from './core/services/v2/fleet.service';
import { GridManagerService } from './core/services/v2/grid-manager.service';
import { NeededActionsService } from './core/services/v2/needed-actions.service';
import { SelectionVinsService } from './core/services/v2/selection-vins.service';
import { VehicleService } from './core/services/vehicle.service';
import { WenConfigurationService } from './core/services/wen.service';
import { DowntimeBannerStore } from './core/stores/downtime-banner.store';
import { BackofficeStore } from './core/stores/backoffice.store';
import { BatteryStatusStore } from './core/stores/battery-status.store';
import { DealerSettingsStore } from './core/stores/dealer-settings.store';
import { DealerStore } from './core/stores/dealer.store';
import { ScreenStateStore } from './core/stores/screen-state.store';
import { UserStore } from './core/stores/user.store';
import { CaseCreationStore } from './core/stores/v2/case-creation.store';
import { CaseInformationStore } from './core/stores/v2/case-information.store';
import { VehicleStore } from './core/stores/vehicle.store';
import { ColumnFactory } from './shared/utils/ag-grid.utils';
import { translationProvider } from './translation.provider';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([httpAuthenticationInterceptor])),
    provideOAuthClient(),
    { provide: CaseInformationStore },
    { provide: UserStore },
    { provide: DealerStore },
    { provide: VehicleStore },
    { provide: ScreenStateStore },
    { provide: DealerSettingsStore },
    { provide: BackofficeStore },
    { provide: BatteryStatusStore },
    { provide: CaseCreationStore },
    { provide: DowntimeBannerStore },
    { provide: NeededActionsService },
    { provide: SelectionVinsService },
    { provide: DealerService },
    { provide: UserService },
    { provide: VehicleService },
    { provide: ModalService },
    { provide: PreConditionService },
    { provide: CaseService },
    { provide: WenConfigurationService },
    { provide: FleetService },
    { provide: GridManagerService },
    { provide: BackofficeService },
    { provide: DowntimeBannerService },
    { provide: ColumnFactory },
    { provide: DealerQueueService },
    { provide: LocaleService },
    importProvidersFrom(
      DsImprintModule.forRoot({
        phone: '123498765',
        electronicContact: 'qwerty@bmwgroup.com',
      }),
      DsToastModule,
      TranslateModule.forRoot(translationProvider())
    ),
  ],
};

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-077466}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Bayerische_Motoren_Werke_AG}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{PURE}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{PURE}_need_to_be_licensed___{PURE}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{21_March_2026}____[v3]_[01]_MTc3NDA1MTIwMDAwMA==f0c76ab7e44d55c110be7b19f64d6b88'
);
ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({ theme: dsAgGridTheme });
registerSupportedLocales();
